import { useCallback, useMemo } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { membersEndpoints as endpoints } from '@/api/endpoints';
import { callDelete, callGet, callPut } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import { selectUserId } from '@/redux/user/selectors';
import { Member, Members, PartialMember } from '@/shared/types/member';

export const API = Object.freeze({
  listMembers: async (): Promise<Members> => callGet(endpoints.members),

  updateMember: async (updates: PartialMember): Promise<Member> =>
    callPut(endpoints.updateMember(updates.user_id), updates),

  deleteMember: async (member: PartialMember): Promise<Member> =>
    callDelete(endpoints.updateMember(member.user_id)),
});

const useMembers = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const accountSlug = useSelector(selectAccountSlug);
  const userId = useSelector(selectUserId);
  const { data: members, status: listStatus } = useQuery<Members, Error>({
    queryKey: [endpoints.members, accountSlug],
    queryFn: () => API.listMembers(),

    enabled: !!accountSlug,
  });

  const { mutate: updateMember, status: updateStatus } = useMutation<Member, Error, PartialMember>({
    mutationFn: API.updateMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.members, accountSlug] });
      toast.success(t('members.member_updated'));
    },
    meta: { errorToast: true },
  });

  const { mutate: deleteMember, status: deleteStatus } = useMutation<Member, Error, PartialMember>({
    mutationFn: API.deleteMember,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [endpoints.members, accountSlug] });
      toast.success(t('members.member_deleted'));
    },
    meta: { errorToast: true },
  });

  const membersById = useMemo(
    () => new Map(members?.members?.map((m) => [m.user_id, m])),
    [members]
  );

  const getMember = useCallback((id: string) => membersById.get(id), [membersById]);

  const member = useMemo(
    () => members?.members?.find((m) => m.user_id === userId),
    [members, userId]
  );

  return {
    members: members?.members,
    member,
    updateMember,
    listStatus,
    updateStatus,
    deleteMember,
    deleteStatus,
    getMember,
  };
};

export default useMembers;
